/**
 * 🟥 CUADRO NEGRO 2026-06-03 — SampleDashboardsPanel ("D de M")
 *
 * Modal que abre el botón "D de M" (Dashboards de Muestra) en el Área Interna
 * de Trabajo. Lista los 32 módulos del owner del proyecto como GALERÍA DE
 * MUESTRAS (read-only) para todos los usuarios (Free y Premium).
 *
 * - Pestañas: Dashboards / Tablas / Custom
 * - Cada card: click → abre visor con el código completo + botón "Copiar todo"
 * - NUNCA permite editar/borrar/duplicar los originales.
 * - El usuario copia el contenido al portapapeles → pega manualmente en su
 *   propio Workspace o en el chat con DamyxIA.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { X, Loader2, Copy, Check, LayoutDashboard, Table2, Code2, Eye, Send } from 'lucide-react';
import { detectMultiFile, parseMultiFile } from '../../utils/parseMultiFile';

const TAB_META = {
  es: {
    dashboard: { label: 'Dashboards', icon: LayoutDashboard },
    table: { label: 'Tablas', icon: Table2 },
    custom: { label: 'Custom', icon: Code2 },
  },
  en: {
    dashboard: { label: 'Dashboards', icon: LayoutDashboard },
    table: { label: 'Tables', icon: Table2 },
    custom: { label: 'Custom', icon: Code2 },
  },
};

const fetchJSON = async (path, opts = {}) => {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); } catch { /* ignore */ }
    document.body.removeChild(ta);
  }
};

const SampleDashboardsPanel = ({ open, onClose, language = 'es' }) => {
  const es = language === 'es';
  const labels = es ? TAB_META.es : TAB_META.en;
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ dashboard: 0, table: 0, custom: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [viewing, setViewing] = useState(null); // módulo en visor
  const [copiedCardId, setCopiedCardId] = useState(null);
  const [copiedViewer, setCopiedViewer] = useState(false);
  // 🟥 CUADRO NEGRO — "Enviar al Workspace" para T de M.
  // sendingId guarda el id del módulo que está en proceso de copia/creación
  // para mostrar spinner en su botón sin bloquear el resto del grid.
  const [sendingId, setSendingId] = useState(null);

  const load = useCallback(async () => {
    // Reset interno del visor y copy-feedback al abrir.
    setViewing(null);
    setCopiedCardId(null);
    setCopiedViewer(false);
    setLoading(true);
    try {
      const data = await fetchJSON('/api/internal/sample-modules');
      setItems(Array.isArray(data?.items) ? data.items : []);
      setCounts(data?.counts || { dashboard: 0, table: 0, custom: 0, total: 0 });
    } catch (e) {
      toast.error(es ? 'No se pudieron cargar las muestras' : 'Could not load samples');
    }
    setLoading(false);
  }, [es]);

  useEffect(() => {
    if (!open) return;
    // queueMicrotask para desacoplar el setState del callback síncrono del
    // effect y satisfacer la regla react-hooks/set-state-in-effect del proyecto.
    const id = setTimeout(() => { load(); }, 0);
    return () => clearTimeout(id);
  }, [open, load]);

  const filteredItems = useMemo(
    () => items.filter((i) => i.type === activeTab),
    [items, activeTab],
  );

  const handleCopyCard = async (mod, e) => {
    e.stopPropagation();
    await copyToClipboard(mod.code || '');
    setCopiedCardId(mod.id);
    toast.success(es ? 'Código copiado' : 'Code copied');
    setTimeout(() => setCopiedCardId((cur) => (cur === mod.id ? null : cur)), 1500);
  };

  const handleCopyViewer = async () => {
    if (!viewing) return;
    const payload = `// ${viewing.name}\n// ${viewing.description || ''}\n\n${viewing.code || ''}`;
    await copyToClipboard(payload);
    setCopiedViewer(true);
    toast.success(es ? 'Contenido copiado' : 'Content copied');
    setTimeout(() => setCopiedViewer(false), 1500);
  };

  /**
   * 🟥 CUADRO NEGRO T de M V2 — Enviar al Workspace con routing automático.
   *
   * Detecta si el contenido de la muestra es multi-archivo (markers `// file:`,
   * múltiples code-fences etiquetados, o headers `## file.ext`). Si lo es,
   * parsea el contenido en {path, content}[] y llama /api/workspace/createProject
   * para tener árbol + edit + ZIP + Push. Si no, mantiene el comportamiento V1
   * llamando /api/workspace/auto.
   *
   * En ambos casos dispara `damyxia-open-internal-module` para que el panel
   * Internal Workspace abra el módulo recién creado.
   */
  const handleSendToWorkspace = async (mod, e) => {
    if (e) e.stopPropagation();
    if (!mod || sendingId) return;
    setSendingId(mod.id);
    try {
      const content = mod.code || '';
      const isMulti = detectMultiFile(content);

      let res;
      let moduleId;
      let moduleName;
      let detectedType;
      let fileCount = 0;

      if (isMulti) {
        const files = parseMultiFile(content);
        if (files.length < 2) {
          // Detector dijo multi pero el parser solo encontró 1 — fallback a auto.
          res = await fetchJSON('/api/workspace/auto', {
            method: 'POST',
            body: JSON.stringify({
              title: mod.name || (es ? 'Muestra' : 'Sample'),
              content,
              source: 'sample',
            }),
          });
          moduleId = res?.module_id;
          moduleName = res?.name || mod.name || (es ? 'Muestra' : 'Sample');
          detectedType = res?.detected_type || 'text';
        } else {
          res = await fetchJSON('/api/workspace/createProject', {
            method: 'POST',
            body: JSON.stringify({
              projectName: mod.name || (es ? 'Muestra' : 'Sample'),
              files,
              description: `Sample T de M (${files.length} files)`,
            }),
          });
          moduleId = res?.module_id;
          moduleName = res?.name || mod.name || (es ? 'Muestra' : 'Sample');
          detectedType = 'project';
          fileCount = res?.file_count || files.length;
        }
      } else {
        res = await fetchJSON('/api/workspace/auto', {
          method: 'POST',
          body: JSON.stringify({
            title: mod.name || (es ? 'Muestra' : 'Sample'),
            content,
            source: 'sample',
          }),
        });
        moduleId = res?.module_id;
        moduleName = res?.name || mod.name || (es ? 'Muestra' : 'Sample');
        detectedType = res?.detected_type || 'text';
      }

      if (!moduleId) {
        toast.error(es ? 'No se pudo crear el módulo' : 'Could not create module');
        return;
      }
      // 🟥 CUADRO NEGRO 2026-06-30 (Copia editable) — marcamos el módulo como
      // "copia de muestra" en runtime. InternalWorkspace lo trackea en un ref
      // para pedir Guardar/Descartar al cerrar si hubo cambios.
      window.dispatchEvent(new CustomEvent('damyxia-open-internal-module', {
        detail: {
          moduleId,
          name: moduleName,
          detectedType,
          isSampleCopy: true,
          sampleName: mod.name || moduleName,
        },
      }));
      toast.success(
        fileCount > 0
          ? (es ? `Proyecto "${moduleName}" creado · ${fileCount} archivos` : `Project "${moduleName}" created · ${fileCount} files`)
          : (es ? `"${moduleName}" enviado al Workspace` : `"${moduleName}" sent to Workspace`)
      );
      setViewing(null);
      onClose?.();
    } catch (err) {
      const msg = (err && err.message) || 'unknown';
      if (/403/.test(msg)) {
        toast.error(es ? 'Enviar al Workspace es Premium' : 'Send to Workspace is Premium-only');
      } else {
        toast.error(es ? `Error al enviar: ${msg}` : `Send failed: ${msg}`);
      }
    } finally {
      setSendingId(null);
    }
  };

  if (!open) return null;

  const tabs = ['dashboard', 'table', 'custom'];

  return (
    <div
      className="fixed inset-0 z-[9000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="sample-dashboards-panel"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-5xl h-[88vh] bg-gradient-to-b from-slate-900 to-slate-950 border border-indigo-500/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-gradient-to-r from-indigo-900/40 to-purple-900/30">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-white">
              {es ? 'Trabajos de Muestras' : 'Sample Works'}
            </h2>
            <p className="text-[11px] text-indigo-300/80">
              {es
                ? `${counts.total} muestras · solo lectura · cópialas a tu Workspace`
                : `${counts.total} samples · read-only · copy them to your Workspace`}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="sample-dashboards-close-btn"
            aria-label={es ? 'Cerrar' : 'Close'}
            title={es ? 'Cerrar' : 'Close'}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-3 pt-3 border-b border-slate-800">
          {tabs.map((t) => {
            const meta = labels[t];
            const Icon = meta.icon;
            const active = activeTab === t;
            const count = counts[t] || 0;
            return (
              <button
                key={t}
                onClick={() => { setActiveTab(t); setViewing(null); }}
                data-testid={`sample-dashboards-tab-${t}`}
                className={`relative flex items-center gap-1.5 px-3 py-2 rounded-t-lg text-[12px] font-medium transition-colors ${
                  active
                    ? 'bg-slate-800 text-white border-x border-t border-slate-700'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {meta.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? 'bg-indigo-500/30 text-indigo-200' : 'bg-slate-800 text-slate-400'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center h-full text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex items-center justify-center h-full text-slate-500 text-sm">
              {es ? 'No hay muestras en esta categoría.' : 'No samples in this category.'}
            </div>
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
              data-testid="sample-dashboards-grid"
            >
              {filteredItems.map((mod) => (
                <div
                  key={mod.id}
                  onClick={() => setViewing(mod)}
                  data-testid={`sample-card-${mod.id}`}
                  className="group cursor-pointer rounded-xl bg-slate-800/60 border border-slate-700/70 hover:border-indigo-500/60 hover:bg-slate-800 transition-all p-3 flex flex-col gap-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-[13px] font-semibold text-white line-clamp-2 flex-1">
                      {mod.name}
                    </h3>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={(e) => handleSendToWorkspace(mod, e)}
                        data-testid={`sample-send-${mod.id}`}
                        disabled={sendingId === mod.id}
                        aria-label={es ? 'Enviar al Workspace' : 'Send to Workspace'}
                        title={es ? 'Enviar al Workspace' : 'Send to Workspace'}
                        className="p-1.5 rounded-md bg-purple-600/80 hover:bg-purple-500 disabled:opacity-60 text-white transition-colors border border-purple-500/40"
                      >
                        {sendingId === mod.id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Send className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={(e) => handleCopyCard(mod, e)}
                        data-testid={`sample-copy-${mod.id}`}
                        aria-label={es ? 'Copiar código' : 'Copy code'}
                        title={es ? 'Copiar código' : 'Copy code'}
                        className="p-1.5 rounded-md bg-slate-900/80 hover:bg-indigo-700 text-slate-300 hover:text-white transition-colors border border-slate-700"
                      >
                        {copiedCardId === mod.id
                          ? <Check className="w-3.5 h-3.5 text-emerald-400" />
                          : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                  {mod.description && (
                    <p className="text-[11px] text-slate-400 line-clamp-2">{mod.description}</p>
                  )}
                  <div className="flex items-center justify-between gap-2 mt-auto pt-1">
                    <span className="text-[10px] text-slate-500 italic">
                      {(mod.code || '').length} {es ? 'caracteres' : 'chars'}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] text-indigo-300 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Eye className="w-3 h-3" /> {es ? 'Ver' : 'View'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer disclaimer */}
        <footer className="px-5 py-2.5 border-t border-slate-800 bg-slate-900/60">
          <p className="text-[11px] text-slate-500 italic text-center">
            {es
              ? 'Solo lectura — copia el código y pégalo en tu Workspace o en el chat con DamyxIA para usarlo o editarlo.'
              : 'Read-only — copy the code and paste it into your Workspace or DamyxIA chat to use or edit it.'}
          </p>
        </footer>
      </div>

      {/* Visor de detalle */}
      {viewing && (
        <div
          className="fixed inset-0 z-[9100] bg-black/80 flex items-center justify-center p-4"
          data-testid="sample-viewer"
          onClick={(e) => { if (e.target === e.currentTarget) setViewing(null); }}
        >
          <div className="w-full max-w-3xl max-h-[90vh] bg-slate-950 border border-indigo-500/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-gradient-to-r from-indigo-900/40 to-purple-900/30">
              <div>
                <h3 className="text-sm font-bold text-white">{viewing.name}</h3>
                <p className="text-[11px] text-indigo-300/80 capitalize">{viewing.type}</p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleSendToWorkspace(viewing)}
                  data-testid="sample-viewer-send-btn"
                  disabled={sendingId === viewing.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 disabled:opacity-60 text-white text-[12px] font-medium transition-colors"
                  aria-label={es ? 'Enviar al Workspace' : 'Send to Workspace'}
                  title={es ? 'Enviar al Workspace y abrir el módulo' : 'Send to Workspace and open the module'}
                >
                  {sendingId === viewing.id
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {es ? 'Enviando…' : 'Sending…'}</>
                    : <><Send className="w-3.5 h-3.5" /> {es ? 'Enviar al Workspace' : 'Send to Workspace'}</>}
                </button>
                <button
                  onClick={handleCopyViewer}
                  data-testid="sample-viewer-copy-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-[12px] font-medium transition-colors"
                  aria-label={es ? 'Copiar todo' : 'Copy all'}
                >
                  {copiedViewer
                    ? <><Check className="w-3.5 h-3.5" /> {es ? 'Copiado' : 'Copied'}</>
                    : <><Copy className="w-3.5 h-3.5" /> {es ? 'Copiar todo' : 'Copy all'}</>}
                </button>
                <button
                  onClick={() => setViewing(null)}
                  data-testid="sample-viewer-close-btn"
                  aria-label={es ? 'Cerrar' : 'Close'}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </header>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {viewing.description && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                    {es ? 'Descripción' : 'Description'}
                  </p>
                  <p className="text-[13px] text-slate-200 whitespace-pre-wrap">{viewing.description}</p>
                </div>
              )}
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                  {es ? 'Código' : 'Code'}
                </p>
                <pre className="text-[11px] text-emerald-200 bg-slate-900 border border-slate-800 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
                  {viewing.code || (es ? '(sin código)' : '(no code)')}
                </pre>
              </div>
              {Array.isArray(viewing.tags) && viewing.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {viewing.tags.map((t, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
                      {String(t)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SampleDashboardsPanel;
